// src/components/fix-actions/full-content-renderer.tsx
// Handles every fullContent shape the backend can return.
//
// Responsive notes:
// - All user-facing text paragraphs include overflowWrap: "anywhere" + wordBreak:
//   "break-word" so long URLs / tokens never overflow narrow card widths.
// - ComparisonTable is wrapped in overflow-x-auto (existing).
// - StructuredDataRenderer's <pre> uses whiteSpace: "pre-wrap" (existing).

import { useState } from "react";
import { Copy, Check, ChevronDown, ChevronRight } from "lucide-react";

interface FullContentRendererProps {
  content: Record<string, any>;
}

// Shared style fragments so long strings don't blow out cards/trays on mobile.
const TEXT_WRAP: React.CSSProperties = {
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

// ─────────────────────────────────────────────────────────────
// Safe string coercion
// ─────────────────────────────────────────────────────────────
function safeStr(val: unknown): string {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val)) return val.map(safeStr).filter(Boolean).join(", ");
  if (typeof val === "object") {
    const o = val as Record<string, unknown>;
    if (typeof o.text === "string") return o.text;
    if (typeof o.value === "string") return o.value;
    if (typeof o.content === "string") return o.content;
    if (typeof o.label === "string") return o.label;
    if (typeof o.name === "string") return o.name;
    if (typeof o.description === "string") return o.description;
    return JSON.stringify(val);
  }
  return String(val);
}

const META_KEYS = new Set(["type", "version", "id", "schema_version"]);

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        });
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 10px",
        backgroundColor: "var(--background)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        fontSize: "var(--text-xs)",
        color: copied ? "var(--alpha-teal)" : "var(--muted-foreground)",
        cursor: "pointer",
        transition: "color 0.15s",
      }}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied!" : label}
    </button>
  );
}

function SectionLabel({ label }: { label: string }) {
  return (
    <div
      style={{
        fontSize: "var(--text-xs)",
        fontWeight: "var(--font-weight-semibold)",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "var(--muted-foreground)",
        marginBottom: "6px",
      }}
    >
      {label.replace(/_/g, " ")}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Generic fallback
// ─────────────────────────────────────────────────────────────
function GenericObjectRenderer({ data }: { data: any }) {
  const entries = Object.entries(data || {}).filter(
    ([key]) => !META_KEYS.has(key.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {entries.map(([key, value]) => {
        if (typeof value === "string" && value.trim()) {
          return (
            <div key={key}>
              <SectionLabel label={key} />
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--foreground)",
                  lineHeight: "1.6",
                  ...TEXT_WRAP,
                }}
              >
                {value}
              </p>
            </div>
          );
        }

        if (Array.isArray(value) && value.length > 0) {
          return (
            <div key={key}>
              <SectionLabel label={key} />
              <div className="space-y-2">
                {value.map((item: any, idx: number) => {
                  if (typeof item === "string") {
                    return (
                      <div key={idx} style={{ display: "flex", gap: "8px" }}>
                        <span style={{ color: "var(--muted-foreground)", flexShrink: 0 }}>•</span>
                        <p
                          style={{
                            fontSize: "var(--text-sm)",
                            color: "var(--foreground)",
                            lineHeight: "1.5",
                            margin: 0,
                            minWidth: 0,
                            ...TEXT_WRAP,
                          }}
                        >
                          {item}
                        </p>
                      </div>
                    );
                  }
                  if (typeof item === "object" && item !== null) {
                    const q = safeStr(item.question || item.scenario || item.q || item.prompt || item.title || item.name || item.statement || item.claim || item.criterion || "");
                    const a = safeStr(item.answer || item.response || item.a || item.guidance || item.description || item.detail || item.context || item.explanation || "");
                    const label = safeStr(item.label || item.source || "");
                    if (q || a) {
                      return (
                        <div
                          key={idx}
                          className="p-3 rounded-lg"
                          style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
                        >
                          {q && (
                            <p
                              style={{
                                fontSize: "var(--text-sm)",
                                fontWeight: "var(--font-weight-semibold)",
                                color: "var(--foreground)",
                                marginBottom: a ? "6px" : 0,
                                ...TEXT_WRAP,
                              }}
                            >
                              {q}
                            </p>
                          )}
                          {a && (
                            <p
                              style={{
                                fontSize: "var(--text-sm)",
                                color: "var(--muted-foreground)",
                                lineHeight: "1.5",
                                margin: 0,
                                ...TEXT_WRAP,
                              }}
                            >
                              {a}
                            </p>
                          )}
                          {label && (
                            <p
                              style={{
                                fontSize: "var(--text-xs)",
                                color: "var(--signal-blue)",
                                marginTop: "4px",
                                ...TEXT_WRAP,
                              }}
                            >
                              {label}
                            </p>
                          )}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={idx}
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
                      >
                        {Object.entries(item)
                          .filter(([k]) => !META_KEYS.has(k.toLowerCase()))
                          .map(([k, v]) => (
                            <p
                              key={k}
                              style={{
                                fontSize: "var(--text-sm)",
                                color: "var(--foreground)",
                                lineHeight: "1.5",
                                margin: 0,
                                ...TEXT_WRAP,
                              }}
                            >
                              <strong>{k.replace(/_/g, " ")}:</strong> {safeStr(v)}
                            </p>
                          ))}
                      </div>
                    );
                  }
                  return (
                    <div
                      key={idx}
                      style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", ...TEXT_WRAP }}
                    >
                      {safeStr(item)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if (typeof value === "object" && value !== null) {
          const o = value as Record<string, unknown>;
          const inline =
            typeof o.text === "string" ? o.text :
            typeof o.value === "string" ? o.value :
            typeof o.content === "string" ? o.content : null;

          if (inline) {
            return (
              <div key={key}>
                <SectionLabel label={key} />
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--foreground)",
                    lineHeight: "1.6",
                    ...TEXT_WRAP,
                  }}
                >
                  {inline}
                </p>
              </div>
            );
          }

          return (
            <div key={key}>
              <SectionLabel label={key} />
              <div className="pl-3 space-y-1" style={{ borderLeft: "2px solid var(--border)" }}>
                {Object.entries(o)
                  .filter(([k]) => !META_KEYS.has(k.toLowerCase()))
                  .map(([k, v]) => (
                    <p
                      key={k}
                      style={{
                        fontSize: "var(--text-sm)",
                        color: "var(--foreground)",
                        lineHeight: "1.5",
                        margin: 0,
                        ...TEXT_WRAP,
                      }}
                    >
                      <strong style={{ color: "var(--muted-foreground)" }}>
                        {k.replace(/_/g, " ")}:
                      </strong>{" "}
                      {safeStr(v)}
                    </p>
                  ))}
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Comparison Table
// ─────────────────────────────────────────────────────────────
function ComparisonTableRenderer({ data }: { data: any }) {
  const columns: string[] = (data?.columns || []).map(safeStr);
  const rows: any[] = data?.rows || [];

  return (
    <div>
      {data?.title && (
        <h4
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "8px",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.title)}
        </h4>
      )}
      {data?.introduction && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginBottom: "20px",
            lineHeight: "1.6",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.introduction)}
        </p>
      )}

      {columns.length > 0 && rows.length > 0 ? (
        <div className="overflow-x-auto -mx-1 px-1">
          <table
            className="w-full border-collapse"
            style={{ fontSize: "var(--text-sm)", minWidth: columns.length > 3 ? 560 : undefined }}
          >
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)" }}>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    className="pb-3 text-left"
                    style={{
                      fontWeight: "var(--font-weight-semibold)",
                      paddingRight: "16px",
                      color: "var(--foreground)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, rIdx: number) => {
                const keys = Object.keys(row);
                return (
                  <tr key={rIdx} style={{ borderBottom: "1px solid var(--border)" }}>
                    {columns.map((_col, cIdx) => (
                      <td
                        key={cIdx}
                        className="py-3"
                        style={{
                          paddingRight: "16px",
                          color: cIdx === 0 ? "var(--foreground)" : "var(--muted-foreground)",
                          fontWeight: cIdx === 0 ? "var(--font-weight-semibold)" : undefined,
                          verticalAlign: "top",
                          ...TEXT_WRAP,
                        }}
                      >
                        {safeStr(row[keys[cIdx]] ?? "")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <GenericObjectRenderer data={data} />
      )}

      {data?.conclusion && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginTop: "20px",
            lineHeight: "1.6",
            fontStyle: "italic",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.conclusion)}
        </p>
      )}
      <div className="flex justify-end mt-3">
        <CopyButton text={JSON.stringify(data, null, 2)} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Decision Framework
// ─────────────────────────────────────────────────────────────
function DecisionFrameworkRenderer({ data }: { data: any }) {
  const steps: any[] = data?.criteria || data?.factors || data?.steps || data?.phases || data?.items || [];

  return (
    <div>
      {data?.title && (
        <h4
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "8px",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.title)}
        </h4>
      )}
      {data?.description && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginBottom: "16px",
            lineHeight: "1.6",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.description)}
        </p>
      )}

      {steps.length > 0 ? (
        <div className="space-y-3">
          {steps.map((item: any, idx: number) => (
            <div
              key={idx}
              className="p-3 rounded-lg"
              style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
            >
              <div
                style={{
                  fontWeight: "var(--font-weight-semibold)",
                  fontSize: "var(--text-sm)",
                  marginBottom: "4px",
                  color: "var(--foreground)",
                  ...TEXT_WRAP,
                }}
              >
                {idx + 1}.{" "}
                {safeStr(item.name || item.criterion || item.step || item.label || item.phase || item.title || `Step ${idx + 1}`)}
              </div>
              {(item.description || item.guidance || item.detail || item.rationale) && (
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--muted-foreground)",
                    lineHeight: "1.5",
                    ...TEXT_WRAP,
                  }}
                >
                  {safeStr(item.description || item.guidance || item.detail || item.rationale)}
                </p>
              )}
              {item.weight !== undefined && (
                <span
                  style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--signal-blue)",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  Weight: {safeStr(item.weight)}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <GenericObjectRenderer data={data} />
      )}

      {data?.conclusion && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginTop: "16px",
            lineHeight: "1.6",
            fontStyle: "italic",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.conclusion)}
        </p>
      )}
      <div className="flex justify-end mt-3">
        <CopyButton text={JSON.stringify(data, null, 2)} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Scenario-Based Q&A
// ─────────────────────────────────────────────────────────────
function ScenarioQARenderer({ data }: { data: any }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  const items: any[] =
    data?.scenarios || data?.qaPairs || data?.qa_pairs ||
    data?.items || data?.questions || data?.faqs || data?.entries || [];

  return (
    <div>
      {data?.title && (
        <h4
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "8px",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.title)}
        </h4>
      )}
      {(data?.introduction || data?.overview || data?.summary) && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginBottom: "16px",
            lineHeight: "1.6",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.introduction || data.overview || data.summary)}
        </p>
      )}

      {items.length > 0 ? (
        <div className="space-y-2">
          {items.map((s: any, idx: number) => {
            const question = safeStr(
              s.question || s.scenario || s.q || s.prompt || s.title || s.name || `Scenario ${idx + 1}`
            );
            const answer = safeStr(s.answer || s.response || s.a || s.guidance || s.description || s.detail || "");
            const context = safeStr(s.context || s.note || s.tip || "");
            const isOpen = openIdx === idx;

            return (
              <div
                key={idx}
                className="rounded-lg border"
                style={{ borderColor: "var(--border)", overflow: "hidden" }}
              >
                <button
                  className="w-full text-left p-3 flex items-start justify-between gap-2"
                  style={{
                    backgroundColor: isOpen ? "var(--alpha-teal-5)" : "var(--muted)",
                    cursor: "pointer",
                    border: "none",
                  }}
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                >
                  <span
                    style={{
                      fontSize: "var(--text-sm)",
                      fontWeight: "var(--font-weight-semibold)",
                      color: "var(--foreground)",
                      minWidth: 0,
                      flex: 1,
                      ...TEXT_WRAP,
                    }}
                  >
                    {question}
                  </span>
                  {isOpen
                    ? <ChevronDown className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--muted-foreground)" }} />
                    : <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--muted-foreground)" }} />}
                </button>
                {isOpen && (
                  <div
                    className="p-3"
                    style={{ backgroundColor: "var(--background)", borderTop: "1px solid var(--border)" }}
                  >
                    {answer && (
                      <p
                        style={{
                          fontSize: "var(--text-sm)",
                          color: "var(--foreground)",
                          lineHeight: "1.6",
                          marginBottom: context ? "8px" : 0,
                          ...TEXT_WRAP,
                        }}
                      >
                        {answer}
                      </p>
                    )}
                    {context && (
                      <p
                        style={{
                          fontSize: "var(--text-xs)",
                          color: "var(--muted-foreground)",
                          lineHeight: "1.5",
                          fontStyle: "italic",
                          ...TEXT_WRAP,
                        }}
                      >
                        {context}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <GenericObjectRenderer data={data} />
      )}

      {data?.conclusion && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginTop: "16px",
            lineHeight: "1.6",
            fontStyle: "italic",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.conclusion)}
        </p>
      )}
      <div className="flex justify-end mt-3">
        <CopyButton text={JSON.stringify(data, null, 2)} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Evidence & Citation Pack
// ─────────────────────────────────────────────────────────────
function EvidenceCitationRenderer({ data }: { data: any }) {
  const claims: any[] =
    data?.claims || data?.evidence || data?.citations ||
    data?.items || data?.entries || data?.facts || data?.points || [];

  return (
    <div>
      {data?.title && (
        <h4
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "8px",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.title)}
        </h4>
      )}
      {(data?.summary || data?.introduction || data?.overview) && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginBottom: "16px",
            lineHeight: "1.6",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.summary || data.introduction || data.overview)}
        </p>
      )}

      {claims.length > 0 ? (
        <div className="space-y-3">
          {claims.map((item: any, idx: number) => (
            <div
              key={idx}
              className="p-3 rounded-lg"
              style={{ backgroundColor: "var(--muted)", borderLeft: "3px solid var(--signal-blue)" }}
            >
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  marginBottom: "4px",
                  color: "var(--foreground)",
                  ...TEXT_WRAP,
                }}
              >
                {safeStr(item.claim || item.statement || item.title || item.point || item.fact || `Point ${idx + 1}`)}
              </p>
              {item.source && (
                <p
                  style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--signal-blue)",
                    marginBottom: "4px",
                    ...TEXT_WRAP,
                  }}
                >
                  Source: {safeStr(item.source)}
                </p>
              )}
              {(item.evidence || item.detail || item.context || item.description) && (
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--muted-foreground)",
                    lineHeight: "1.5",
                    ...TEXT_WRAP,
                  }}
                >
                  {safeStr(item.evidence || item.detail || item.context || item.description)}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <GenericObjectRenderer data={data} />
      )}

      {data?.conclusion && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginTop: "16px",
            lineHeight: "1.6",
            fontStyle: "italic",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.conclusion)}
        </p>
      )}
      <div className="flex justify-end mt-3">
        <CopyButton text={JSON.stringify(data, null, 2)} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Structured Data / Schema
// ─────────────────────────────────────────────────────────────
function StructuredDataRenderer({ data }: { data: any }) {
  const payload = data?.schema || data?.structuredData || data?.jsonLd || data?.json_ld || data;
  const jsonStr = JSON.stringify(payload, null, 2);

  return (
    <div>
      {data?.title && (
        <h4
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "8px",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.title)}
        </h4>
      )}
      {data?.description && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginBottom: "16px",
            lineHeight: "1.6",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.description)}
        </p>
      )}
      <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
        <div
          className="px-3 py-2 flex items-center justify-between gap-2"
          style={{ backgroundColor: "var(--muted)", borderBottom: "1px solid var(--border)" }}
        >
          <span
            style={{
              fontSize: "var(--text-xs)",
              color: "var(--muted-foreground)",
              fontWeight: "var(--font-weight-semibold)",
            }}
          >
            JSON-LD / Structured Data
          </span>
          <CopyButton text={jsonStr} label="Copy JSON" />
        </div>
        <pre
          style={{
            margin: 0,
            padding: "16px",
            fontSize: "var(--text-xs)",
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            overflowX: "auto",
            backgroundColor: "var(--background)",
            color: "var(--foreground)",
            lineHeight: "1.6",
          }}
        >
          {jsonStr}
        </pre>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Boundary / Trust Artifact
// ─────────────────────────────────────────────────────────────
function BoundaryTrustRenderer({ data }: { data: any }) {
  const statements: any[] =
    data?.statements || data?.boundaries || data?.trustSignals ||
    data?.trust_signals || data?.items || data?.policies || [];

  return (
    <div>
      {data?.title && (
        <h4
          style={{
            fontSize: "var(--text-lg)",
            fontWeight: "var(--font-weight-semibold)",
            marginBottom: "8px",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.title)}
        </h4>
      )}
      {(data?.rationale || data?.introduction || data?.overview) && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginBottom: "16px",
            lineHeight: "1.6",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.rationale || data.introduction || data.overview)}
        </p>
      )}

      {statements.length > 0 ? (
        <div className="space-y-3">
          {statements.map((s: any, idx: number) => (
            <div
              key={idx}
              className="p-3 rounded-lg"
              style={{ backgroundColor: "var(--muted)", borderLeft: "3px solid var(--alpha-teal)" }}
            >
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  marginBottom: "2px",
                  color: "var(--foreground)",
                  ...TEXT_WRAP,
                }}
              >
                {safeStr(s.statement || s.boundary || s.signal || s.title || s.claim || s.policy || `Statement ${idx + 1}`)}
              </p>
              {(s.explanation || s.context || s.detail || s.description) && (
                <p
                  style={{
                    fontSize: "var(--text-sm)",
                    color: "var(--muted-foreground)",
                    lineHeight: "1.5",
                    ...TEXT_WRAP,
                  }}
                >
                  {safeStr(s.explanation || s.context || s.detail || s.description)}
                </p>
              )}
            </div>
          ))}
        </div>
      ) : data?.content ? (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--foreground)",
            lineHeight: "1.6",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.content)}
        </p>
      ) : (
        <GenericObjectRenderer data={data} />
      )}

      {data?.conclusion && (
        <p
          style={{
            fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
            marginTop: "16px",
            lineHeight: "1.6",
            fontStyle: "italic",
            ...TEXT_WRAP,
          }}
        >
          {safeStr(data.conclusion)}
        </p>
      )}
      <div className="flex justify-end mt-3">
        <CopyButton text={JSON.stringify(data, null, 2)} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main exported renderer
// ─────────────────────────────────────────────────────────────
export function FullContentRenderer({ content }: FullContentRendererProps) {
  if (!content || Object.keys(content).length === 0) {
    return (
      <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
        Content preview not available for this asset.
      </p>
    );
  }

  const typeStr = String(content.type || "").toLowerCase();
  const data: Record<string, any> =
    content.data && typeof content.data === "object" ? content.data : content;

  const hasQAArray = !!(data?.scenarios || data?.qaPairs || data?.qa_pairs || data?.questions || data?.faqs || data?.entries);
  const hasEvidenceArray = !!(data?.claims || data?.citations || data?.facts || data?.points);
  const hasFrameworkArray = !!(data?.criteria || data?.factors || data?.steps || data?.phases);
  const hasBoundaryArray = !!(data?.boundaries || data?.trustSignals || data?.trust_signals || data?.policies);

  if (typeStr.includes("comparison") || data?.columns) {
    return <ComparisonTableRenderer data={data} />;
  }
  if (typeStr.includes("decision") || typeStr.includes("framework") || hasFrameworkArray) {
    return <DecisionFrameworkRenderer data={data} />;
  }
  if (typeStr.includes("scenario") || typeStr.includes("qa") || typeStr.includes("q&a") || typeStr.includes("faq") || hasQAArray) {
    return <ScenarioQARenderer data={data} />;
  }
  if (typeStr.includes("evidence") || typeStr.includes("citation") || hasEvidenceArray) {
    return <EvidenceCitationRenderer data={data} />;
  }
  if (typeStr.includes("structured") || typeStr.includes("schema") || data?.schema || data?.structuredData || data?.jsonLd) {
    return <StructuredDataRenderer data={data} />;
  }
  if (typeStr.includes("boundary") || typeStr.includes("trust") || hasBoundaryArray) {
    return <BoundaryTrustRenderer data={data} />;
  }
  if (typeStr === "data artifact" || typeStr === "data-artifact" || typeStr === "data_artifact") {
    return <StructuredDataRenderer data={data} />;
  }

  return (
    <div>
      <GenericObjectRenderer data={data} />
      <div className="flex justify-end mt-4">
        <CopyButton text={JSON.stringify(data, null, 2)} />
      </div>
    </div>
  );
}